
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import AppLayout from "@/components/layout/AppLayout";
import { AlertTriangle, ChevronLeft, Home, MessageCircle, Users } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [errorId] = useState(`err-${Math.random().toString(36).substring(2, 9)}`);

  // Check if the path contains team chat
  const isTeamChatPath = location.pathname.includes('/applications/team/') && 
                         location.pathname.includes('/chat');
  
  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname,
      "User Agent:",
      navigator.userAgent,
      "Error ID:",
      errorId
    );
    
    // Show toast for helpful feedback
    toast({
      title: t('pageNotFound'),
      description: t('pageNotFoundMessage'),
      variant: "destructive",
      duration: 5000,
    });
  }, [location.pathname, errorId, toast]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleTeamsClick = () => {
    navigate("/teams");
  };

  return (
    <AppLayout>
      <div className="min-h-[calc(100vh-10rem)] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg border-red-100">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-center mb-4">
              <AlertTriangle className="h-12 w-12 text-red-500" />
            </div>
            <h1 className="text-3xl font-bold text-center">404</h1>
            <p className="text-xl text-gray-600 text-center">
              {t('pageNotFound')}
            </p>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-md text-sm border border-gray-100">
              <p className="font-mono text-gray-500 break-all">
                {t('path')}: {location.pathname}
              </p>
              {errorId && (
                <p className="font-mono text-gray-500 mt-1 text-xs">
                  {t('reference')}: {errorId}
                </p>
              )}
            </div>
            
            {isTeamChatPath && (
              <div className="bg-blue-50 p-3 rounded-md border border-blue-100">
                <p className="text-blue-700 text-sm">
                  <strong>{t('lookingForTeamChat')}</strong> {t('teamChatNotExistMessage')}
                </p>
              </div>
            )}
          </CardContent>
          
          <CardFooter className="flex flex-col space-y-2">
            <Button 
              onClick={handleGoBack}
              className="w-full"
              variant="outline"
              size="lg"
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              {t('goBack')}
            </Button>
            
            <div className="grid grid-cols-2 gap-2 w-full">
              <Button 
                asChild
                variant="default"
                size="lg"
              >
                <Link to="/">
                  <Home className="mr-2 h-4 w-4" />
                  {t('home')}
                </Link>
              </Button>
              
              <Button 
                onClick={handleTeamsClick}
                variant="secondary"
                size="lg"
              >
                <Users className="mr-2 h-4 w-4" />
                {t('teams')}
              </Button>
            </div>
            
            {isTeamChatPath && (
              <Button 
                variant="default" 
                className="w-full mt-2 bg-blue-600 hover:bg-blue-700"
                onClick={() => navigate("/applications")}
              >
                <MessageCircle className="mr-2 h-4 w-4" />
                {t('allApplications')}
              </Button>
            )}
          </CardFooter>
        </Card>
      </div>
    </AppLayout>
  );
};

export default NotFound;
